<template>
  <span>
    <v-btn depressed color="#1F7A52" dark height="44" :disabled="!columns.length" @click="open">
      <v-icon left>mdi-download</v-icon>
      ดาวน์โหลด Excel
    </v-btn>

    <v-dialog v-model="dialog" max-width="520" :persistent="building">
      <v-card>
        <v-card-title class="tw-flex tw-items-start tw-gap-3 tw-flex-nowrap">
          <v-avatar size="40" color="#E6F4EC" rounded>
            <v-icon color="#1E6B45">mdi-file-excel-outline</v-icon>
          </v-avatar>
          <div class="tw-leading-tight tw-flex-1">
            <div class="tw-text-lg tw-font-bold">ดาวน์โหลด Excel</div>
            <div class="tw-text-xs tw-text-gray-600 tw-font-normal tw-mt-1">
              {{ bankCode }} · หน่วย {{ unit }}
            </div>
          </div>
          <v-btn icon :disabled="building" aria-label="ปิด" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="tw-pt-5 tw-text-gray-900">
          <div class="field-label">ช่วงวันที่</div>
          <v-chip-group v-model="preset" mandatory column>
            <!-- Selected = filled dark chip via props: Vuetify forces outlined
                 chips transparent with !important, so an active-class
                 background never shows and the text went white on white. -->
            <v-chip
              v-for="p in presetChips"
              :key="p.key"
              :value="p.key"
              :outlined="preset !== p.key"
              :color="preset === p.key ? '#1A1D23' : undefined"
              :text-color="preset === p.key ? 'white' : undefined"
              :disabled="building"
            >
              {{ p.label }}
            </v-chip>
          </v-chip-group>

          <div class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mt-2">
            <v-menu v-model="fromMenu" :close-on-content-click="true" offset-y min-width="292px" :disabled="building">
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="formatThaiDate(from)"
                  label="จากวันที่"
                  append-icon="mdi-calendar-month-outline"
                  readonly
                  outlined
                  dense
                  hide-details
                  :disabled="building"
                  v-on="on"
                />
              </template>
              <v-date-picker :value="from" :max="today" color="primary" locale="en-in" no-title @input="pickFrom" />
            </v-menu>
            <v-menu v-model="toMenu" :close-on-content-click="true" offset-y min-width="292px" :disabled="building">
              <template v-slot:activator="{ on }">
                <v-text-field
                  :value="formatThaiDate(to)"
                  label="ถึงวันที่"
                  append-icon="mdi-calendar-month-outline"
                  readonly
                  outlined
                  dense
                  hide-details
                  :disabled="building"
                  v-on="on"
                />
              </template>
              <v-date-picker :value="to" :max="today" color="primary" locale="en-in" no-title @input="pickTo" />
            </v-menu>
          </div>

          <div class="field-label tw-mt-5">รูปแบบไฟล์</div>
          <v-radio-group v-model="format" hide-details class="tw-mt-0 tw-pt-0" :disabled="building">
            <label v-for="f in formats" :key="f.value" :class="['format-option', { 'format-option--on': format === f.value }]">
              <v-radio :value="f.value" color="#1F7A52" class="tw-mb-0" />
              <span class="tw-flex tw-flex-col">
                <b class="tw-font-semibold">{{ f.title }}</b>
                <span class="tw-text-xs tw-text-gray-600">{{ f.hint }}</span>
              </span>
            </label>
          </v-radio-group>

          <div class="summary">
            <v-progress-circular v-if="checking" size="18" width="2" indeterminate color="#5B6270" />
            <v-icon v-else small :color="error ? 'error' : '#5B6270'">
              {{ error ? "mdi-alert-circle-outline" : "mdi-information-outline" }}
            </v-icon>
            <span>{{ summaryText }}</span>
          </div>
          <div v-if="!checking && !error && missing.length" class="tw-text-xs tw-text-gray-600 tw-mt-2">
            วันที่ไม่มีข้อมูล: {{ missingPreview }}
          </div>
        </v-card-text>

        <v-divider />
        <v-card-actions class="tw-px-6 tw-py-4">
          <v-spacer />
          <v-btn outlined height="44" :disabled="building" @click="dialog = false">ยกเลิก</v-btn>
          <v-btn
            depressed
            color="#1F7A52"
            dark
            height="44"
            :loading="building"
            :disabled="!canDownload"
            @click="download"
          >
            <v-icon left>mdi-download</v-icon>
            ดาวน์โหลด
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import * as XLSX from "xlsx";
import moment from "moment";
import firebase from "firebase/app";
import fc from "@/firebaseConfig";
import {
  bankColumns,
  primaryColumn,
  rateUnit,
  rangeError,
  buildRateSheet,
  buildSapSheet,
  exportFileName,
  datePresets,
  missingDates,
  formatThaiDate,
} from "@/libs/exchangeRateExport";

const CHECK_DEBOUNCE_MS = 300;
const MISSING_PREVIEW = 5;

export default {
  name: "ExchangeRateExportDialog",
  props: {
    bankCode: { type: String, required: true },
  },
  data() {
    const today = moment().format("YYYY-MM-DD");
    const presets = datePresets(today);
    return {
      dialog: false,
      fromMenu: false,
      toMenu: false,
      today,
      presets,
      preset: presets[0].key,
      from: presets[0].from,
      to: presets[0].to,
      format: "all",
      // Docs for the current range, fetched when it changes so the summary
      // and the download use the same read.
      docs: [],
      checking: false,
      building: false,
      error: "",
      checkTimer: null,
      requestId: 0,
    };
  },
  computed: {
    presetChips() {
      return this.presets.concat([{ key: "custom", label: "กำหนดเอง" }]);
    },
    columns() {
      return bankColumns(this.bankCode);
    },
    primaryBuying() {
      return primaryColumn(this.bankCode, "BUYING");
    },
    unit() {
      return rateUnit(this.bankCode) === "KIP" ? "กีบ" : "บาท";
    },
    formats() {
      const buy = this.primaryBuying ? this.primaryBuying.colName : "";
      return [
        {
          value: "all",
          title: "ทุกช่องเรต",
          hint: `${this.columns.map((c) => c.colName).join(", ")} และเวลาอัปเดต · 1 แถวต่อสกุลเงินต่อวัน`,
        },
        { value: "sap", title: "แบบตาราง SAP", hint: `1 แถวต่อวัน 1 คอลัมน์ต่อสกุลเงิน ใช้อัตราซื้อ ${buy}` },
      ];
    },
    missing() {
      return missingDates(this.from, this.to, this.docs.map((d) => d.id));
    },
    missingPreview() {
      const shown = this.missing.slice(0, MISSING_PREVIEW).map(formatThaiDate).join(", ");
      const more = this.missing.length - MISSING_PREVIEW;
      return more > 0 ? `${shown} และอีก ${more} วัน` : shown;
    },
    summaryText() {
      if (this.error) return this.error;
      if (this.checking) return "กำลังตรวจข้อมูล…";
      if (!this.docs.length) return `ไม่พบอัตราแลกเปลี่ยน ${this.bankCode} ในช่วงนี้`;
      const missing = this.missing.length ? `ขาด ${this.missing.length} วัน` : "ไม่มีวันที่ขาด";
      return `พบข้อมูล ${this.docs.length} วัน · ${missing}`;
    },
    canDownload() {
      return !this.checking && !this.error && this.docs.length > 0;
    },
  },
  watch: {
    preset(key) {
      const p = this.presets.find((x) => x.key === key);
      if (!p) return;
      this.from = p.from;
      this.to = p.to;
      this.scheduleCheck();
    },
    bankCode() {
      this.docs = [];
      if (this.dialog) this.scheduleCheck();
    },
  },
  beforeDestroy() {
    clearTimeout(this.checkTimer);
  },
  methods: {
    formatThaiDate,
    open() {
      this.dialog = true;
      this.scheduleCheck();
    },
    pickFrom(value) {
      this.from = value;
      this.preset = "custom";
      this.scheduleCheck();
    },
    pickTo(value) {
      this.to = value;
      this.preset = "custom";
      this.scheduleCheck();
    },
    scheduleCheck() {
      clearTimeout(this.checkTimer);
      this.error = rangeError(this.from, this.to) || "";
      if (this.error) {
        this.docs = [];
        return;
      }
      this.checking = true;
      this.checkTimer = setTimeout(this.check, CHECK_DEBOUNCE_MS);
    },
    async check() {
      const requestId = ++this.requestId;
      try {
        // Doc IDs are YYYY-MM-DD, so an ID range is a date range.
        const snapshot = await fc.db
          .collection(`ExchangeRateCenter/FX-Rate/${this.bankCode}`)
          .where(firebase.firestore.FieldPath.documentId(), ">=", this.from)
          .where(firebase.firestore.FieldPath.documentId(), "<=", this.to)
          .get();
        if (requestId !== this.requestId) return;
        this.docs = snapshot.docs.map((doc) => ({ id: doc.id, data: doc.data() }));
        this.error = "";
      } catch (e) {
        if (requestId !== this.requestId) return;
        this.docs = [];
        this.error = (e && e.message) || String(e);
      } finally {
        if (requestId === this.requestId) this.checking = false;
      }
    },
    download() {
      this.building = true;
      try {
        const sheet =
          this.format === "sap"
            ? buildSapSheet(this.docs, this.primaryBuying)
            : buildRateSheet(this.docs, this.columns);
        const ws = XLSX.utils.aoa_to_sheet([sheet.header].concat(sheet.rows));
        for (let r = 1; r <= sheet.rows.length; r++) {
          sheet.rateCols.forEach((c) => {
            const cell = ws[XLSX.utils.encode_cell({ r, c })];
            if (cell && typeof cell.v === "number") cell.z = "#,##0.00####";
          });
        }
        ws["!cols"] = sheet.header.map((h) => ({ wch: Math.max(12, h.length + 4) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, this.bankCode);
        XLSX.writeFile(wb, exportFileName(this.bankCode, this.from, this.to, this.format));
        this.dialog = false;
      } catch (e) {
        this.error = (e && e.message) || String(e);
      } finally {
        this.building = false;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.field-label {
  font-size: 13px;
  font-weight: 600;
  color: #3d4450;
  margin-bottom: 4px;
}

.format-option {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 52px;
  padding: 6px 12px;
  margin-bottom: 8px;
  border: 1px solid #e3e6eb;
  border-radius: 10px;
  cursor: pointer;
}

.format-option--on {
  border-color: #1f7a52;
  background: #f3faf6;
}

.summary {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 10px;
  background: #f7f8fa;
  font-size: 13px;
  color: #3d4450;
}
</style>
